import _ from "lodash";
import * as moment from "moment-timezone";
import * as React from "react";
import {
  useHistory,
  useRouteMatch
} from "react-router-dom";
import { toast } from "react-toastify";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";
import * as yup from "yup";

import { GLOBAL_SETTINGS_CACHE_KEY } from "src/core/shared/api/globalSettings.api";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import useCache from "src/core/shared/hooks/useCache";
import { IStore } from "src/core/shared/store";
import { useCurrentUser } from "src/core/users/hooks";
import { Button } from "src/library/components/Button";
import { DayRangePicker } from "src/library/components/DayRangePicker";
import { ComponentDates } from "src/library/components/DayRangePicker/DayRangePicker";
import {
  CheckboxLabel,
  CheckboxStyled,
  FormRow,
  Label,
  Radio
} from "src/library/components/Forms";
import Input from "src/library/components/Forms/Input";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { Modal } from "src/library/components/Modal";
import { Text } from "src/library/components/Text";
import { Tooltip } from "src/library/components/Tooltip";
import { useWindowSize } from "src/library/hooks/useWindowSize";

import {
  IShutdown,
  useShutdownsApi
} from "../api/shutdown.api";
import { useDropShutdowns } from "../hooks/useDropShutdowns";

interface IScheduleFormProps {
  initialShutdown: IShutdown | null;
  dropId?: number;
  disableExtendLeadTimes?: boolean;
}

interface UrlParams {
  dropId?: string;
  customerId?: string;
  shutdownId?: string;
}

const ShutdownForm: React.ReactFCWithChildren<IScheduleFormProps> = props => {
  const { initialShutdown, dropId } = props;
  const history = useHistory();
  const { removeItemByKey } = useCache();
  const match = useRouteMatch<UrlParams>();
  const [ { currentRole } ] = useCurrentUser();
  const { orderVariants } = useMappedState((store: IStore) => ({ orderVariants: store.global.settings?.orderVariants || [] }));
  const [ showShutdownWarning, setShowShutdownWarning ] = React.useState(false);
  const [ createShutdownRes, createShutdownReq ] = useShutdownsApi("CREATE_SHUTDOWN");
  const [ updateShutdownRes, updateShutdownReq ] = useShutdownsApi("UPDATE_SHUTDOWN");
  const { width } = useWindowSize();
  const { checkShutdownDate, shutdownDates } = useDropShutdowns();

  const [ validation, setValidation ] = React.useState<{ error: boolean; message: string }>({
    error: false,
    message: ""
  });

  const dateFormat = width && width < 786 ? "DD/MM/YY" : "ddd DD MMM YYYY";

  const [ form, setForm ] = React.useState<Partial<IShutdown>>(initialShutdown ? {
    ...initialShutdown,
    startDate: moment.utc(initialShutdown.startDate).toDate(),
    endDate: moment.utc(initialShutdown.endDate).toDate()
  } : { extendLeadTime: false });

  // handles the form events to set the values
  const handleFormChange = React.useCallback(
    (name: keyof IShutdown, value: any) => {
      setValidation({
        error: false,
        message: ""
      });

      if (name === "cancelExistingOrders") {
        setForm(currentForm => ({
          ...currentForm,
          [ name ]: value === "1" ? true : false
        }));
      } else if (name === "extendLeadTime") {
        setForm(currentForm => ({
          ...currentForm,
          [ name ]: value === "on" ? true : false
        }));
      } else {
        setForm(currentForm => ({
          ...currentForm,
          [ name ]: value
        }));
      }
    },
    [ ]
  );

  // performs the relevant api request based on id
  const saveShudown = React.useCallback(() => {
    const reqParams: Partial<IShutdown> = {
      ...form,
      branchId: dropId
    };

    if (form.id) {
      updateShutdownReq({
        shutdownId: form.id,
        shutdownUpdate: reqParams
      });
    } else {
      createShutdownReq(reqParams as IShutdown);
    }
  }, [
    form,
    createShutdownReq,
    updateShutdownReq,
    dropId
  ]);

  // handles the submit button event
  const handleSubmit = React.useCallback(() => {
    shutdownSchema
      .validate(form)
      .then(() => {
        if (form.cancelExistingOrders) {
          setShowShutdownWarning(true);
        } else {
          saveShudown();
        }
      })
      .catch(err => {
        setValidation({
          error: true,
          message: err.errors
        });
      });
  }, [ form, saveShudown ]);

  React.useEffect(() => {
    if (createShutdownRes.data || updateShutdownRes.data) {
      toast.success("Saved successfully");
      removeItemByKey(GLOBAL_SETTINGS_CACHE_KEY);

      if (createShutdownRes.data?.branchId || updateShutdownRes.data?.branchId) {
        if (currentRole?.type === "admin") {
          if (match.params.customerId && match.params.dropId) {
            history.push(`/admin/customers/${match.params.customerId}/drops/${match.params.dropId}/shutdowns`);
          }
        } else {
          history.push("/shutdowns");
        }
      } else {
        history.push("/platform/shutdowns");
      }
    }

    if (createShutdownRes.error || updateShutdownRes.error) {
      toast.error((createShutdownRes.error ? createShutdownRes.error.message : null) || (updateShutdownRes.error ? updateShutdownRes.error.message : "Error"));
    }
  }, [
    createShutdownRes,
    currentRole?.type,
    history,
    match?.params.customerId,
    match?.params.dropId,
    updateShutdownRes,
    removeItemByKey
  ]);

  /** Modifier adds a dot to the calendar to show an existing shutdown exists */
  const modifiers = React.useMemo(() => ({
    withActiveDate: (day: Date) => {
      const shutdownDetails = checkShutdownDate(day);

      return !!shutdownDetails?.shutdown.branchId;
    }
  }), [ checkShutdownDate ]);

  /** Returns a list of dates that should be disabled */
  const disableShutdownDates: ComponentDates[] = React.useMemo(() => {
    const dates: ComponentDates[] = shutdownDates.filter(sd => {
      if (sd.shutdown.id === initialShutdown?.id) {
        return false;
      }

      if (dropId) {
        return !!sd.shutdown.branchId;
      } else {
        return !sd.shutdown.branchId;
      }
    }).map(sd => {
      const dateDetails: ComponentDates = {
        date: moment.utc(sd.date, "YYYY-MM-DD").toDate(),
        component: (
          <>
            <Text>
              This date is already blocked for ordering due to another holiday:
              {" "}

              {sd.shutdown.reason}
            </Text>
          </>
        )
      };

      return dateDetails;
    });

    return dates;
  }, [
    dropId,
    initialShutdown?.id,
    shutdownDates
  ]);

  const onVariantChange = React.useCallback((variantId: number) => {
    const currentVariants = form.options?.excludeVariants || [];

    if (currentVariants.includes(variantId)) {
      handleFormChange("options", {
        ...form.options,
        excludeVariants: _.filter(currentVariants, variant => variant !== variantId)
      });
    } else {
      handleFormChange("options", ({
        ...form.options,
        excludeVariants: [ ...currentVariants, variantId ]
      }));
    }
  }, [ form.options, handleFormChange ]);

  return (
    <FormContainer>
      <Modal
        backgroundAction={() => setShowShutdownWarning(false)}
        show={showShutdownWarning}
        title="Are you sure?"
        small={false}
        body="This action may cancel existing orders and cannot be undone."
        actions={[
          {
            name: "No, cancel",
            $buttonType: "secondary",
            action: () => setShowShutdownWarning(false)
          },
          {
            name: "I confirm",
            $buttonType: "primary",
            action: () => saveShudown()
          }
        ]}
      />

      {validation.error && (
        <ValidationContainer>
          <Icon
            $fillColor="error"
            icon={Icons.ERRORCIRCLE}
          />

          {validation.message}
        </ValidationContainer>
      )}

      <FormRow>
        <Input
          as="input"
          handleChange={e => handleFormChange("reason", e.target.value)}
          name="reason"
          label="Reason for shutdown"
          inputType="text"
          defaultValue={initialShutdown ? initialShutdown.reason : ""}
        />
      </FormRow>

      <FormRow>
        <Label
          label="Cancel or block orders"
          for="cancelExistingOrders"
        />

        <ErrorNotice
          type="warn"
          message="If you select to cancel orders, when this form is saved the platform will cancel all orders that fall within the date range selected"
        />

        <Radio
          options={[
            {
              text: "Cancel and block orders on this date",
              value: "1",
              checked: form.cancelExistingOrders === true
            },
            {
              text: "Block orders only",
              value: "0",
              checked: form.cancelExistingOrders === false
            }
          ]}
          handleChange={e => handleFormChange("cancelExistingOrders", e.target.value)}
          name="cancelExistingOrders"
          label=""
          value={typeof form.cancelExistingOrders === "boolean" ? form.cancelExistingOrders ? "1" : "0" : ""}
        />
      </FormRow>

      {orderVariants.length > 1 && (
        <FormRow>
          <Label
            label="Apply to order variants"
            for="excludeVariants"
          />

          <ErrorNotice
            message="If selected, customers will not be able to place an order for that order variant on the selected shutdown dates"
            type="info"
          />

          {orderVariants.map(ov => (
            <CheckboxLabel
              key={`order-variant-${ov.code}`}
              for={`order-variant-${ov.code}`}
              className="order-variant"
              label={ov.name}
            >
              <CheckboxStyled
                name={`order-variant-${ov.code}`}
                id={`order-variant-${ov.code}`}
                checked={!form.options?.excludeVariants?.includes(ov.id)}
                onChange={() => {
                  onVariantChange(ov.id);
                }}
              />

            </CheckboxLabel>

          ))}
        </FormRow>
      )}

      <FormRow>

        {!props.disableExtendLeadTimes && (
          <>
            <div
              data-tip
              data-for="extend-lead-time-help"
              className="label-with-help"
            >
              <Label
                label="Extend Lead Times"
                for="extendLeadTime"
              />

              <Icon
                icon={Icons.HELP}
                size={10}
              />
            </div>

            <CheckboxStyled
              name="extendLeadTime"
              onChange={e => handleFormChange("extendLeadTime", e.target.value)}
              checked={form.extendLeadTime}
            />

            <Tooltip
              id="extend-lead-time-help"
              type="dark"
              place="top"
              getContent={() => {
                return (
                  <Text color="white">
                    This option will extend lead times by the number of days in the shutdown for orders where the lead time would be within this shutdown.
                  </Text>
                );
              }}
            />
          </>
        )}
      </FormRow>

      <FormRow style={{ position: "relative" }}>
        <Label
          label="Select start and end date"
          for="date-range"
        />

        <SelectedDates>
          <Icon
            size={18}
            icon={Icons.CALENDAR}
            $fillColor="midGrey"
          />

          <Text>
            {form && form.startDate && form.endDate ?
              `${moment.utc(form.startDate).format(dateFormat)} - ${moment.utc(form.endDate).format(dateFormat)}` :
              "Dates"}
          </Text>
        </SelectedDates>

        <DayRangePickerWrapper>
          <DayRangePicker
            maxDate={moment.utc().add(2, "years").toDate()}
            date={(form && form.startDate) || new Date()}
            minDate={new Date()}
            modifiers={modifiers}
            disabledDates={disableShutdownDates}
            initialDateRange={initialShutdown ? {
              from: moment.utc(initialShutdown.startDate).startOf("day").toDate(),
              to: moment.utc(initialShutdown.endDate).endOf("day").toDate()
            } : null}
            handleDateChange={({ from, to }) => {
              if (from) {
                handleFormChange("startDate", from);
              }

              if (to) {
                handleFormChange("endDate", to);
              }
            }}
          />
        </DayRangePickerWrapper>

      </FormRow>

      <SubmitContainer>
        <Button
          loading={createShutdownRes.pending || updateShutdownRes.pending}
          $buttonType="primary"
          onClick={() => handleSubmit()}
        >
          <>
            {initialShutdown ? "Update shutdown" : "Create shutdown"}
          </>
        </Button>
      </SubmitContainer>
    </FormContainer>
  );
};

const ValidationContainer = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md} 0;
    color: ${theme.colors.error};
    display: flex;
    align-items: center;

    svg {
      margin-right: ${theme.spacing.padding.sm};
    }
  `}
`;

const DayRangePickerWrapper = styled.div`
 .DayPicker {
  width: 225px;
 }
`;

const SubmitContainer = styled.div`

  ${({ theme }) => css`
    padding-top: ${theme.spacing.padding.md};
    > * {
      margin: 0 ${theme.spacing.padding.xs};
    }

    > button:first-child {
      margin: 0 -${theme.spacing.padding.xs};
    }
  `}
`;

const FormLine = styled.div`

  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md} 0;
  `}
`;

const FormContainer = styled.div`
  .label-with-help {
    display: flex;
    flex-direction: row;
  }
  &.suspended ${FormLine} {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const SelectedDates = styled.div`
  display: flex;
  flex-direction: row;
  > span {
    ${({ theme }) => css`
      margin-left: ${theme.spacing.padding.xs};
    `}
  }
`;

export const shutdownSchema = yup.object().shape({
  reason: yup.string().required(),
  cancelExistingOrders: yup.bool().required(),
  startDate: yup
    .date()
    .label("Start date")
    .required(),
  endDate: yup
    .date()
    .label("End date")
    .required()
    .when("startDate", (startDate: Date, schema: yup.DateSchema) => {
      if (startDate) {
        return schema.min(startDate);
      } else {
        return schema;
      }
    })
});

export default ShutdownForm;
